import React, { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Tag,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Descriptions,
  Typography,
  Alert,
  Tooltip,
} from 'antd';
import {
  ReloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  SyncOutlined,
  LinkOutlined,
  LogoutOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CrownOutlined,
  UserOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { groupAPI, botAPI } from '../services/api';
import { ManagedGroup, GroupStatistics, BotConfig } from '../types';
import GroupMessageModal from '../components/GroupMessageModal';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text, Paragraph } = Typography;

const GroupManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<ManagedGroup[]>([]);
  const [bots, setBots] = useState<BotConfig[]>([]);
  const [statistics, setStatistics] = useState<GroupStatistics | null>(null);
  const [selectedBotId, setSelectedBotId] = useState<number | undefined>();

  const [addModalVisible, setAddModalVisible] = useState(false);
  const [inviteLinkModalVisible, setInviteLinkModalVisible] = useState(false);
  const [messageModalVisible, setMessageModalVisible] = useState(false);
  const [currentGroup, setCurrentGroup] = useState<ManagedGroup | null>(null);

  const [form] = Form.useForm();
  const [inviteLinkForm] = Form.useForm();

  // 获取群组列表
  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await groupAPI.getAll(selectedBotId);
      setGroups(response.data);
    } catch (error: any) {
      message.error('获取群组列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取机器人列表
  const fetchBots = async () => {
    try {
      const response = await botAPI.getAll();
      setBots(response.data);
    } catch (error: any) {
      message.error('获取机器人列表失败');
    }
  };

  // 获取统计信息
  const fetchStatistics = async () => {
    try {
      const response = await groupAPI.getStatistics(selectedBotId);
      setStatistics(response.data);
    } catch (error: any) {
      console.error('获取统计信息失败:', error);
    }
  };

  useEffect(() => {
    fetchBots();
    fetchGroups();
    fetchStatistics();
  }, [selectedBotId]);

  // 添加群组
  const handleAdd = async (values: any) => {
    console.log('📝 提交的表单数据:', values);
    console.log('📝 groupId 类型:', typeof values.groupId);
    try {
      await groupAPI.add(values);
      message.success('群组添加成功！');
      setAddModalVisible(false);
      form.resetFields();
      fetchGroups();
      fetchStatistics();
    } catch (error: any) {
      console.error('❌ API 错误:', error);
      console.error('❌ 错误响应:', error.response);
      const errorMsg = error.response?.data?.error || '添加失败';
      const errorDetail = error.response?.data?.detail;
      if (errorDetail) {
        message.error(`${errorMsg}：${errorDetail}`);
      } else {
        message.error(errorMsg);
      }
    }
  };

  // 刷新群组信息
  const handleRefresh = async (id: number) => {
    try {
      await groupAPI.refresh(id);
      message.success('群组信息已刷新');
      fetchGroups();
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || '刷新失败';
      const errorDetail = error.response?.data?.detail;
      if (errorDetail) {
        message.error(`${errorMsg}：${errorDetail}`);
      } else {
        message.error(errorMsg);
      }
    }
  };

  // 生成邀请链接
  const openInviteLinkModal = (group: ManagedGroup) => {
    setCurrentGroup(group);
    setInviteLinkModalVisible(true);
  };

  const handleGenerateInviteLink = async (values: any) => {
    if (!currentGroup) return;

    try {
      const response = await groupAPI.generateInviteLink(currentGroup.id, values);
      message.success('邀请链接生成成功！');
      setInviteLinkModalVisible(false);
      inviteLinkForm.resetFields();

      // 显示邀请链接
      Modal.success({
        title: '邀请链接',
        content: (
          <div>
            <Paragraph copyable>{response.data.inviteLink}</Paragraph>
            {response.data.expireDate && (
              <Text type="secondary">
                过期时间：{new Date(response.data.expireDate * 1000).toLocaleString()}
              </Text>
            )}
            {response.data.memberLimit && (
              <Text type="secondary">人数限制：{response.data.memberLimit}</Text>
            )}
          </div>
        ),
      });

      fetchGroups();
    } catch (error: any) {
      message.error(error.response?.data?.error || '生成邀请链接失败');
    }
  };

  // 删除群组（软删除 - 仅移除后台管理）
  const handleDelete = async (id: number, groupTitle: string) => {
    Modal.confirm({
      title: '移除群组管理',
      content: (
        <div>
          <p>确定要从后台移除 <strong>{groupTitle}</strong> 吗？</p>
          <Alert
            message="说明"
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>仅移除后台管理记录</li>
                <li>机器人不会退出群组</li>
                <li>群组数据和交易记录仍保留</li>
                <li>随时可以重新添加回来</li>
              </ul>
            }
            type="info"
            showIcon
            style={{ marginTop: 12 }}
          />
        </div>
      ),
      okText: '确定移除',
      cancelText: '取消',
      onOk: async () => {
        try {
          await groupAPI.delete(id); // 默认 mode=soft
          message.success('群组已从后台移除（数据仍保留）');
          fetchGroups();
          fetchStatistics();
        } catch (error: any) {
          message.error(error.response?.data?.error || '移除失败');
        }
      },
    });
  };

  // 完全清理群组（硬删除 - 危险操作）
  const handleHardDelete = async (id: number, groupTitle: string) => {
    Modal.confirm({
      title: '⚠️ 完全清理群组（危险操作）',
      icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div>
          <Alert
            message="警告：此操作不可撤销！"
            description={
              <div>
                <p style={{ fontWeight: 'bold', marginBottom: 8 }}>将执行以下操作：</p>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>✅ 机器人退出群组</li>
                  <li>✅ 删除后台管理记录</li>
                  <li>✅ 删除 grouplist 记录</li>
                  <li>✅ 删除所有交易记录（永久丢失）</li>
                </ul>
                <p style={{ color: '#ff4d4f', marginTop: 12, marginBottom: 0 }}>
                  <strong>所有数据将永久丢失，无法恢复！</strong>
                </p>
              </div>
            }
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <p style={{ marginBottom: 8 }}>
            请输入群组名称 <Tag color="red">{groupTitle}</Tag> 确认删除：
          </p>
          <Input
            id="confirm-group-title"
            placeholder="输入群组名称"
            onPressEnter={(e) => {
              const input = (e.target as HTMLInputElement).value;
              if (input === groupTitle) {
                Modal.destroyAll();
                performHardDelete(id, groupTitle);
              } else {
                message.error('群组名称不匹配，请重新输入');
              }
            }}
          />
        </div>
      ),
      okText: '确认完全清理',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => {
        const input = (document.getElementById('confirm-group-title') as HTMLInputElement)?.value;
        if (input !== groupTitle) {
          message.error('群组名称不匹配，请重新输入');
          return Promise.reject();
        }
        return performHardDelete(id, groupTitle);
      },
    });
  };

  // 执行完全清理
  const performHardDelete = async (id: number, groupTitle: string) => {
    try {
      await groupAPI.delete(id, 'hard'); // mode=hard
      message.success('群组已完全清理，所有数据已删除');
      fetchGroups();
      fetchStatistics();
    } catch (error: any) {
      message.error(error.response?.data?.error || '清理失败');
    }
  };

  // 退出群组
  const handleLeave = async (id: number) => {
    try {
      await groupAPI.leave(id);
      message.success('机器人已退出群组');
      fetchGroups();
      fetchStatistics();
    } catch (error: any) {
      message.error(error.response?.data?.error || '退出失败');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: '群组信息',
      width: 250,
      render: (_: any, record: ManagedGroup) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.group_title}</Text>
          {record.group_username && (
            <Text type="secondary" copyable>
              @{record.group_username}
            </Text>
          )}
          <Text type="secondary">ID: {record.group_id}</Text>
        </Space>
      ),
    },
    {
      title: '类型',
      dataIndex: 'group_type',
      width: 100,
      render: (type: string) => {
        const config: Record<string, { color: string; text: string }> = {
          group: { color: 'blue', text: '群组' },
          supergroup: { color: 'purple', text: '超级群组' },
          channel: { color: 'orange', text: '频道' },
        };
        const { color, text } = config[type] || { color: 'default', text: type };
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '成员数',
      dataIndex: 'member_count',
      width: 80,
      render: (count: number) => (
        <Space>
          <TeamOutlined />
          {count}
        </Space>
      ),
    },
    {
      title: '机器人权限',
      dataIndex: 'is_bot_admin',
      width: 100,
      render: (isAdmin: number) =>
        isAdmin ? (
          <Tag icon={<CrownOutlined />} color="success">
            管理员
          </Tag>
        ) : (
          <Tag icon={<UserOutlined />} color="default">
            普通成员
          </Tag>
        ),
    },
    {
      title: '邀请链接',
      dataIndex: 'invite_link',
      width: 120,
      render: (link: string, record: ManagedGroup) =>
        link ? (
          <Paragraph
            copyable={{ text: link }}
            style={{ margin: 0, maxWidth: 100 }}
            ellipsis={{ rows: 1 }}
          >
            {link}
          </Paragraph>
        ) : record.is_bot_admin ? (
          <Button
            size="small"
            type="link"
            icon={<LinkOutlined />}
            onClick={() => openInviteLinkModal(record)}
          >
            生成
          </Button>
        ) : (
          <Text type="secondary">无权限</Text>
        ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      ellipsis: true,
      render: (desc: string) => desc || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      width: 180,
      render: (time: string) => new Date(time).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      width: 250,
      fixed: 'right' as const,
      render: (_: any, record: ManagedGroup) => (
        <Space size="small" wrap>
          <Tooltip title="查看群组消息">
            <Button
              size="small"
              icon={<MessageOutlined />}
              onClick={() => {
                setCurrentGroup(record);
                setMessageModalVisible(true);
              }}
            >
              消息
            </Button>
          </Tooltip>
          {record.is_bot_admin && (
            <Button
              size="small"
              icon={<LinkOutlined />}
              onClick={() => openInviteLinkModal(record)}
            >
              邀请链接
            </Button>
          )}
          <Popconfirm
            title="确定要退出此群组吗？"
            description="机器人将退出群组，记录将被删除"
            onConfirm={() => handleLeave(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button size="small" danger icon={<LogoutOutlined />}>
              退出
            </Button>
          </Popconfirm>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id, record.group_title)}
          >
            移除
          </Button>
          <Tooltip title="完全清理：退出群组+删除所有数据">
            <Button
              size="small"
              danger
              type="primary"
              icon={<DeleteOutlined />}
              onClick={() => handleHardDelete(record.id, record.group_title)}
            >
              完全清理
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Title level={3} style={{ margin: 0 }}>
          群组管理
        </Title>
        <Space>
          <Select
            style={{ width: 200 }}
            placeholder="筛选机器人"
            allowClear
            value={selectedBotId}
            onChange={setSelectedBotId}
          >
            {bots.map((bot) => (
              <Option key={bot.id} value={bot.id}>
                {bot.bot_name}
              </Option>
            ))}
          </Select>
          <Button icon={<ReloadOutlined />} onClick={fetchGroups}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalVisible(true)}>
            添加群组
          </Button>
        </Space>
      </div>

      {/* 统计卡片 */}
      {statistics && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>
              <Statistic title="总群组数" value={statistics.total_groups} prefix={<TeamOutlined />} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="启用中"
                value={statistics.active_groups}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="机器人为管理员"
                value={statistics.bot_admin_groups}
                prefix={<CrownOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="总成员数" value={statistics.total_members} prefix={<UserOutlined />} />
            </Card>
          </Col>
        </Row>
      )}

      {/* 群组列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={groups}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* 添加群组弹窗 */}
      <Modal
        title="添加群组"
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Alert
          message="添加群组说明"
          description={
            <div>
              <p>1. 请先在 Telegram 中创建群组</p>
              <p>2. 将机器人添加到群组中</p>
              <p>3. 获取群组 ID（可通过 @getidsbot 获取）</p>
              <p>4. 在下方填写群组 ID 和相关信息</p>
              <p>
                <strong>注意：</strong>群组 ID 是负数（如 -1001234567890）
              </p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={form} layout="vertical" onFinish={handleAdd}>
          <Form.Item
            label="选择机器人"
            name="botId"
            rules={[{ required: true, message: '请选择机器人' }]}
          >
            <Select placeholder="选择机器人">
              {bots
                .filter((bot) => bot.is_running)
                .map((bot) => (
                  <Option key={bot.id} value={bot.id}>
                    {bot.bot_name}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="群组 ID"
            name="groupId"
            rules={[
              { required: true, message: '请输入群组 ID' },
              {
                validator: (_, value) => {
                  if (!value || value < 0) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('群组 ID 必须是负数'));
                },
              },
            ]}
            extra="群组 ID 是负数，例如：-1001234567890"
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="-1001234567890"
              controls={false}
              stringMode={false}
            />
          </Form.Item>

          <Form.Item label="描述" name="description">
            <TextArea rows={3} placeholder="群组用途说明（可选）" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                添加
              </Button>
              <Button onClick={() => setAddModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 生成邀请链接弹窗 */}
      <Modal
        title={`生成邀请链接 - ${currentGroup?.group_title}`}
        open={inviteLinkModalVisible}
        onCancel={() => {
          setInviteLinkModalVisible(false);
          inviteLinkForm.resetFields();
        }}
        footer={null}
      >
        <Form form={inviteLinkForm} layout="vertical" onFinish={handleGenerateInviteLink}>
          <Form.Item label="人数限制" name="memberLimit" extra="留空表示无限制">
            <Input type="number" placeholder="例如：100" min={1} max={99999} />
          </Form.Item>

          <Form.Item
            label="过期时间"
            name="expireDate"
            extra="UNIX 时间戳（秒），留空表示永久有效"
          >
            <Input
              type="number"
              placeholder="例如：1704038400"
              min={Math.floor(Date.now() / 1000)}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                生成链接
              </Button>
              <Button onClick={() => setInviteLinkModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 消息记录弹窗 */}
      {currentGroup && (
        <GroupMessageModal
          visible={messageModalVisible}
          onCancel={() => {
            setMessageModalVisible(false);
            setCurrentGroup(null);
          }}
          groupId={currentGroup.group_id}
          groupTitle={currentGroup.group_title}
        />
      )}
    </div>
  );
};

export default GroupManagement;
